// Shared thumbnail-first video player for private/public media playback with optional event logging.
// Mistake note: Parent screens needed a deterministic playback-start hook; expose one callback to avoid duplicating custom players.
import React, { useState, useEffect, useRef } from "react";
import { getImageUrl, getVideoThumbnailImageUrl } from "helpers/Image.helper";
import { getVideoUrl } from "helpers/Video.helper";
import { getVideoPresignedUrl, createVideoLog } from "api/common/Data.api";

interface VideoPlayerProps {
    video: any;
    className?: string;
    onVideoEnd?: () => void;
    onVideoError?: (error: any) => void;
    onPlayStart?: () => void;
    enableVideoLog?: boolean; // 비디오 로그 활성화 옵션
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
    video,
    className = "w-full rounded-lg",
    onVideoEnd,
    onVideoError,
    onPlayStart,
    enableVideoLog = true
}) => {
    const [videoUrl, setVideoUrl] = useState<string>("");
    const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [aspectRatio, setAspectRatio] = useState<number | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const sessionIdRef = useRef<string>(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    const lastTimeUpdateRef = useRef<number>(0);
    const currentTimeRef = useRef<number>(0);
    const durationRef = useRef<number>(0);

    // 비디오 이벤트 로그 생성
    const logVideoEvent = async (eventType: string, currentTime: number = 0) => {
        if (!enableVideoLog || !video?.id) return;

        try {
            await createVideoLog({
                videoId: video.id,
                sessionId: sessionIdRef.current,
                eventType,
                currentTime
            });
            console.log(`비디오 이벤트 로그 생성: ${eventType}`, {
                videoId: video.id,
                sessionId: sessionIdRef.current,
                currentTime
            });
        } catch (error) {
            console.error('비디오 이벤트 로그 생성 실패:', error);
        }
    };

    // 비디오 URL 가져오기
    const getVideoUrlByPublicStatus = async (videoObj: any) => {
        try {
            const isPrivate =
                videoObj?.isPublic === false ||
                videoObj?.isPublic === 0 ||
                videoObj?.isPublic === "0";
            if (isPrivate) {
                // private 영상은 presigned URL 사용
                const presignedUrlResult = await getVideoPresignedUrl(videoObj.id);
                const presignedUrl = presignedUrlResult.result;
                if (presignedUrl) {
                    setVideoUrl(presignedUrl);
                    return presignedUrl;
                }
            }
            // public 영상이거나 presigned URL을 가져올 수 없는 경우 기본 getVideoUrl 사용
            const defaultUrl = getVideoUrl(videoObj);
            setVideoUrl(defaultUrl);
            return defaultUrl;
        } catch (error) {
            console.error('비디오 URL 가져오기 실패:', error);
            // 오류 발생 시 기본 getVideoUrl 사용
            const fallbackUrl = getVideoUrl(videoObj);
            setVideoUrl(fallbackUrl);
            return fallbackUrl;
        }
    };

    // 썸네일 URL 가져오기
    const getThumbnailUrl = async (thumbnailImage: any) => {
        console.log("getThumbnailUrl", { thumbnailImage });
        try {
            if (thumbnailImage) {
                // 썸네일이 있는 경우 getVideoThumbnailImageUrl 사용
                const url = await getVideoThumbnailImageUrl(thumbnailImage, 400, 300);
                console.log("썸네일 URL 가져오기 성공:", { url, thumbnailImage });
                setThumbnailUrl(url);
                return url;
            }
            console.log("썸네일 이미지가 없음, 기본 이미지 사용");
            setThumbnailUrl("/imgs/common/video-thumbnail-empty.png");
            return "/imgs/common/video-thumbnail-empty.png";
        } catch (error) {
            console.error('썸네일 URL 가져오기 실패:', error);
            setThumbnailUrl("/imgs/common/video-thumbnail-empty.png");
            return "/imgs/common/video-thumbnail-empty.png";
        }
    };

    // 비디오 재생 시작
    const handleVideoPlay = async (event: React.MouseEvent) => {
        event.stopPropagation();

        if (!video) return;

        setIsLoading(true);

        try {
            // 비디오 URL이 없으면 먼저 가져오기

            await getVideoUrlByPublicStatus(video);

            // 썸네일 URL이 없으면 가져오기
            if (!thumbnailUrl && video.thumbnailImage) {
                await getThumbnailUrl(video.thumbnailImage);
            }

            // 재생 상태로 변경
            setIsPlaying(true);
            onPlayStart?.();

            // 비디오 재생 로그 생성
            // await logVideoEvent('play', 0);
        } catch (error) {
            console.error('비디오 재생 준비 실패:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 비디오 정지
    const handleVideoStop = async (event: React.MouseEvent) => {
        event.stopPropagation();
        const currentTime = videoRef.current?.currentTime || 0;
        setIsPlaying(false);

        // 비디오 정지 로그 생성
        await logVideoEvent('pause', currentTime);
    };

    // 비디오 종료 시
    const handleVideoEnd = async () => {
        const currentTime = videoRef.current?.currentTime || 0;
        setIsPlaying(false);

        // 비디오 종료 로그 생성
        await logVideoEvent('ended', currentTime);
        currentTimeRef.current = 0;
        console.log("handleVideoEnd currentTimeRef.current", currentTimeRef.current);

        onVideoEnd?.();
    };

    // 비디오 오류 시
    const handleVideoError = async (error: any) => {
        console.error('비디오 로드 실패:', error);
        const currentTime = videoRef.current?.currentTime || 0;
        setIsPlaying(false);

        // 비디오 오류 로그 생성
        await logVideoEvent('error', currentTime);

        onVideoError?.(error);
    };

    // 컴포넌트 마운트 시 썸네일 URL 가져오기
    useEffect(() => {
        if (video?.thumbnailImage) {
            getThumbnailUrl(video.thumbnailImage);
        }
    }, [video]); // thumbnailUrl 의존성 제거

    // 컴포넌트 언마운트 시 비디오 종료 로그 생성
    useEffect(() => {
        return () => {
            // 컴포넌트 언마운트 시 비디오가 재생 중이거나 currentTime이 0이 아닌 경우 종료 로그 생성
            if (enableVideoLog && video?.id) {
                const currentTime = currentTimeRef.current;
                console.log("currentTime", currentTime);
                console.log("videoRef.current?.duration", durationRef.current);
                if ((isPlaying || currentTime > 0) && currentTime != durationRef.current) {
                    logVideoEvent('leave', currentTime);
                }
            }
        };
    }, [isPlaying, enableVideoLog, video?.id]);

    if (!video) return null;

    return (
        <div className="relative">
            {isPlaying ? (
                // 재생 중일 때는 비디오 플레이어 표시
                <div 
                    className="relative rounded-lg overflow-hidden"
                    style={aspectRatio ? { aspectRatio: `${aspectRatio}` } : {}}
                >
                    <video
                        ref={videoRef}
                        src={videoUrl}
                        poster={thumbnailUrl}
                        controls
                        controlsList="nodownload"
                        playsInline
                        autoPlay
                        className={`${className} w-full h-full object-contain`}
                        preload="metadata"
                        onEnded={handleVideoEnd}
                        onError={handleVideoError}

                        // onSeeked={async () => {
                        //     const currentTime = videoRef.current?.currentTime || 0;
                        //     currentTimeRef.current = currentTime;
                        //     if (currentTime != durationRef.current) {
                        //         await logVideoEvent('seeked', currentTime);
                        //     }
                        // }}
                        onPause={async () => {
                            const currentTime = videoRef.current?.currentTime || 0;
                            currentTimeRef.current = currentTime;
                            if (currentTime != durationRef.current) {
                                await logVideoEvent('pause', currentTime);
                            }
                        }}
                        onPlay={async () => {
                            const currentTime = videoRef.current?.currentTime || 0;
                            currentTimeRef.current = currentTime;
                            await logVideoEvent('play', currentTime);
                        }}
                        onTimeUpdate={async () => {
                            // 30초마다 재생 위치 로그 (너무 빈번한 로그 방지)
                            const currentTime = videoRef.current?.currentTime || 0;
                            currentTimeRef.current = currentTime;
                            const timeDiff = Math.floor(currentTime) - lastTimeUpdateRef.current;

                            if (timeDiff >= 10 && currentTime > 0) {
                                lastTimeUpdateRef.current = Math.floor(currentTime);
                                await logVideoEvent('timeupdate', currentTime);
                            }
                        }}
                        onCanPlay={() => {
                            durationRef.current = videoRef.current?.duration || 0;
                            console.log("onCanPlay durationRef.current", durationRef.current);
                            // 비디오 비율 계산
                            if (videoRef.current) {
                                const width = videoRef.current.videoWidth;
                                const height = videoRef.current.videoHeight;
                                if (width > 0 && height > 0) {
                                    setAspectRatio(width / height);
                                }
                            }
                        }}
                        onLoadedMetadata={() => {
                            // 비디오 메타데이터 로드 시 비율 계산
                            if (videoRef.current) {
                                const width = videoRef.current.videoWidth;
                                const height = videoRef.current.videoHeight;
                                if (width > 0 && height > 0) {
                                    setAspectRatio(width / height);
                                }
                            }
                        }}
                    />
                </div>
            ) : (
                // 정지 상태일 때는 썸네일 + 재생 버튼 표시
                <div
                    className={`relative rounded-lg overflow-hidden cursor-pointer group hover:opacity-80 transition-opacity ${className}`}
                    onClick={handleVideoPlay}
                >
                    <img
                        src={thumbnailUrl || "/imgs/common/video-thumbnail-empty.png"}
                        alt="Video thumbnail"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            console.log("썸네일 이미지 로드 실패, 기본 이미지로 대체");
                            const target = e.target as HTMLImageElement;
                            target.src = "/imgs/common/video-thumbnail-empty.png";
                        }}
                    />

                    {/* 로딩 상태 표시 */}
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                        </div>
                    )}

                    {/* 재생 버튼 오버레이 */}
                    {!isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-all duration-200">
                            <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                                <svg className="w-8 h-8 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VideoPlayer;
